import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingDown, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { money, monthLabel } from "../lib/format";
import { Panel, Empty } from "./Widgets";

export function ResidualAlerts({ month, agentId, className = "" }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("/alerts", { params: { ...(month ? { month } : {}), ...(agentId ? { agent_id: agentId } : {}) } }).then((r) => live && setD(r.data));
    return () => { live = false; };
  }, [month, agentId]);
  if (!d) return null;
  return (
    <Panel className={className} title="Residual alerts" action={<span className="text-xs font-mono text-slate-500">{d.previous ? `vs ${monthLabel(d.previous)}` : ""}</span>}>
      {!d.previous ? <Empty>Alerts appear once there are two months to compare.</Empty> : d.alerts.length === 0 ? <Empty>No sharp drops this month.</Empty> : (
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-800" data-testid="residual-alerts">
          {d.alerts.map((a) => (
            <div key={a.merchant_id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`alert-${a.merchant_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm"><TrendingDown size={14} className="text-rose-400 shrink-0" /><span className="truncate">{a.merchant_name || a.merchant_id}</span>
                  {a.missing && <span className="text-[10px] font-mono text-amber-300">NOT IN REPORT</span>}
                  {a.moved && <span className="text-[10px] font-mono text-blue-300">MOVED AGENT</span>}</div>
                <div className="text-xs text-slate-500 font-mono truncate">{a.merchant_id}{!agentId && ` · ${a.owner}`}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-sm text-rose-400">{a.change_pct}%</div>
                <div className="font-mono text-[11px] text-slate-500">{money(a.before)} → {money(a.now)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export function Leaderboard({ agents }) {
  const [by, setBy] = useState("payout");
  const ranked = [...agents].filter((a) => a.merchants).sort((x, y) => y[by] - x[by]);
  const max = Math.max(1, ...ranked.map((a) => a[by]));
  const tab = (k, label) => (
    <button data-testid={`leaderboard-${k}-tab`} onClick={() => setBy(k)}
      className={`text-xs px-2.5 py-1 rounded transition-colors ${by === k ? "bg-blue-500/15 text-blue-300" : "text-slate-500 hover:text-slate-200"}`}>{label}</button>
  );
  return (
    <Panel title="Agent leaderboard" action={<div className="flex gap-1">{tab("payout", "Payout")}{tab("new_accounts", "New accounts")}</div>}>
      {ranked.length === 0 ? <Empty>No agent activity this month.</Empty> : (
        <div className="p-5 space-y-3" data-testid="leaderboard">
          {ranked.map((a, i) => (
            <div key={a.agent_id} data-testid={`leader-${a.agent_code}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="flex items-center gap-2">
                  <span className={`w-5 text-center font-mono text-xs ${i === 0 ? "text-amber-300" : "text-slate-500"}`}>{i === 0 ? <Trophy size={13} className="inline" /> : i + 1}</span>
                  <Link to={`/admin/agents/${a.agent_id}`} className="hover:text-blue-300">{a.name}</Link>
                </span>
                <span className="font-mono text-emerald-400">{by === "payout" ? money(a.payout) : `${a.new_accounts} new`}</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-800 ml-7 overflow-hidden">
                <div className="h-full bg-emerald-500/80 transition-[width] duration-700" style={{ width: `${(a[by] / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export function MonthComparison({ d }) {
  if (!d.previous) return null;
  const rows = d.agents.filter((a) => a.payout || a.prev_payout);
  const total = rows.reduce((s, a) => ({ now: s.now + a.payout, prev: s.prev + (a.prev_payout || 0) }), { now: 0, prev: 0 });
  const delta = (now, prev) => {
    const diff = now - prev;
    const p = prev ? (diff / Math.abs(prev)) * 100 : null;
    const cls = diff > 0 ? "text-emerald-400" : diff < 0 ? "text-rose-400" : "text-slate-500";
    return <><td className={`text-right font-mono ${cls}`}>{diff >= 0 ? "+" : "-"}{money(Math.abs(diff))}</td>
      <td className={`text-right font-mono ${cls}`}>{p == null ? "new" : `${diff >= 0 ? "▲" : "▼"} ${Math.abs(p).toFixed(1)}%`}</td></>;
  };
  return (
    <Panel title={`Month comparison · ${monthLabel(d.month)} vs ${monthLabel(d.previous)}`} className="mb-6">
      <div className="overflow-x-auto">
        <table className="tbl w-full" data-testid="month-comparison-table">
          <thead><tr><th>Agent</th><th className="!text-right">{monthLabel(d.previous)}</th><th className="!text-right">{monthLabel(d.month)}</th><th className="!text-right">Change</th><th className="!text-right">%</th><th className="!text-right">Merchants</th></tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.agent_id} data-testid={`compare-${a.agent_code}`}>
                <td>{a.name}</td>
                <td className="text-right font-mono text-slate-400">{money(a.prev_payout)}</td>
                <td className="text-right font-mono">{money(a.payout)}</td>
                {delta(a.payout, a.prev_payout || 0)}
                <td className="text-right font-mono text-slate-400">{a.prev_merchants} → {a.merchants}</td>
              </tr>
            ))}
            <tr className="font-semibold"><td>Total payouts</td><td className="text-right font-mono text-slate-400">{money(total.prev)}</td><td className="text-right font-mono">{money(total.now)}</td>{delta(total.now, total.prev)}<td /></tr>
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

export function LostMerchants({ onOpen }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/lost-merchants").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  return (
    <Panel title="Lost merchants" action={d.previous && <span className="text-xs font-mono text-slate-500">in {monthLabel(d.previous)}, gone in {monthLabel(d.month)}</span>}>
      {!d.previous ? <Empty>Lost merchants appear once there are two months to compare.</Empty> : d.lost.length === 0 ? <Empty>No merchants dropped out this month.</Empty> : (
        <>
          <div className="px-5 py-3 text-sm text-slate-400 border-b border-slate-800" data-testid="lost-summary">{d.lost.length} merchant{d.lost.length === 1 ? "" : "s"} · <span className="text-rose-400 font-mono">{money(d.lost_residual)}</span> residual last month</div>
          <table className="tbl w-full" data-testid="lost-merchants-table">
            <thead><tr><th>Merchant</th><th>Owner</th><th className="!text-right">Last volume</th><th className="!text-right">Last residual</th></tr></thead>
            <tbody>{d.lost.map((m) => (
              <tr key={m.merchant_id} className="cursor-pointer" onClick={() => onOpen(m.merchant_id)} data-testid={`lost-${m.merchant_id}`}>
                <td><div>{m.merchant_name || "—"}</div><div className="text-xs font-mono text-slate-500">{m.merchant_id}</div></td>
                <td className="text-slate-300">{m.owner}</td>
                <td className="text-right font-mono text-slate-400">{money(m.last_volume)}</td>
                <td className="text-right font-mono text-rose-300">{money(m.last_residual)}</td>
              </tr>
            ))}</tbody>
          </table>
        </>
      )}
    </Panel>
  );
}
